import { FloatingFormItem } from '@/components/_common';
import { useCompanyMutation } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Col, Form, Row, Select } from 'antd';
import { FormInstance } from 'rc-field-form';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import useAddressQueries from '@/hooks/useAddressQuery';

interface Props {
  form: FormInstance<{ ufCode?: string; cityId?: number; cityName?: string }>;
  companyId?: number;
  cityId?: number;
  disabled?: boolean;
}

const InvoiceAddressFields = ({ form, companyId, cityId, disabled }: Props) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const intl = useIntl();

  const { data: company, mutateAsync: getCompany } = useCompanyMutation();

  const { state, city, zipCode } = useAddressQueries();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (companyId) {
      getCompany(companyId);
    }
  }, [companyId]);

  useEffect(() => {
    if (cityId) {
      city.setSelectedCityId(cityId);
    }
  }, [cityId]);

  useEffect(() => {
    if (company?.companyAddress.zip) {
      zipCode.setSelectedZipCode(company.companyAddress.zip);
    }

    if (
      zipCode.data?.zipCode.replace(/[^0-9]/g, '') ===
      company?.companyAddress.zip
    ) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [company]);

  useEffect(() => {
    if (zipCode.data && !cityId) {
      form.setFieldsValue({
        ufCode: zipCode.data?.state,
        cityId: zipCode.data.cityId,
        cityName: zipCode.data.cityName,
      });

      state.setSelectedStateCode(zipCode.data?.state);
    }
    setLoading(false);
  }, [zipCode.data]);

  useEffect(() => {
    if (city.selectedCity) {
      form.setFieldsValue({
        ufCode: city.selectedCity?.state,
        cityId: city.selectedCity?.id,
        cityName: city.selectedCity?.name,
      });

      state.setSelectedStateCode(city.selectedCity?.state);
    }
    setLoading(false);
  }, [city.selectedCity]);

  return (
    <Row gutter={[16, 9]}>
      <Col xs={24} md={12}>
        <FloatingFormItem
          name="ufCode"
          label={intl.formatMessage({ id: 'invoice.domain.uf' })}
          rules={[defaultEmptyFieldError]}
          getValueFromEvent={(value) => {
            state.setSelectedStateCode(value);
            return value;
          }}
        >
          <Select
            allowClear
            filterOption={(input, option) =>
              (option?.label as string)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            loading={state.isFetching || loading}
            style={{ width: '100%' }}
            options={state.list.map((state) => ({
              value: state.code,
              label: state.code,
            }))}
            disabled={disabled}
          />
        </FloatingFormItem>
      </Col>

      <Form.Item hidden name={'cityId'} />

      <Col xs={24} md={12}>
        <FloatingFormItem
          name="cityName"
          label={intl.formatMessage({
            id: 'invoice.domain.municipalityOccurrence',
          })}
          rules={[defaultEmptyFieldError]}
        >
          <Select
            allowClear
            filterOption={(input, option) =>
              (option?.label as string)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            loading={city.isFetching || loading}
            style={{ width: '100%' }}
            options={city.list.map((city) => ({
              value: city.id,
              label: city.name,
            }))}
            onChange={(value) => {
              form.setFieldValue('cityId', value);
            }}
            disabled={disabled}
          />
        </FloatingFormItem>
      </Col>
    </Row>
  );
};

export default InvoiceAddressFields;
